import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { ContactForm } from "../components/ContactForm";
import { SERVICE_OPTIONS } from "../data/services";

export default function Contact() {
    const [params] = useSearchParams();
    const service = params.get("service") || "";
    const validService = SERVICE_OPTIONS.some((o) => o.value === service) ? service : "";

    return (
        <>
            <Seo
                title="Contact"
                description="Tell Innovated Global IT Solutions about your business need — Infor systems, digital marketing, software development or AI — and our team will get back to you."
            />

            <section className="bg-grid relative overflow-hidden py-20 lg:py-24" data-testid="contact-hero">
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{ background: "radial-gradient(900px 480px at 80% 0%, rgba(230,0,126,0.10), transparent 60%), radial-gradient(700px 420px at 10% 100%, rgba(0,102,255,0.12), transparent 60%)" }}
                />
                <div className="container-x relative max-w-3xl">
                    <Reveal>
                        <p className="eyebrow text-cyan-300">Contact</p>
                        <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                            Let's talk about what's next for your business.
                        </h1>
                        <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">
                            A system to fix, a campaign to run, an application to build, or an honest conversation
                            about AI — send us a message and we'll take it from there.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-16 lg:py-24" data-testid="contact-body">
                <div className="container-x grid gap-12 lg:grid-cols-5">
                    <Reveal className="lg:col-span-2">
                        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Reach us directly</h2>
                        <div className="mt-8 space-y-5">
                            <div className="glass flex gap-4 rounded-2xl p-5" data-testid="contact-address-card">
                                <MapPin size={20} className="mt-0.5 shrink-0 text-cyan-300" aria-hidden="true" />
                                <div>
                                    <h3 className="text-sm font-semibold">Office</h3>
                                    <p className="mt-1 text-sm leading-relaxed text-slate-400">
                                        Neena Thapa Road, Kunraghat,<br />
                                        Gorakhpur, Uttar Pradesh 273008, India
                                    </p>
                                </div>
                            </div>
                            <div className="glass flex gap-4 rounded-2xl p-5" data-testid="contact-phone-card">
                                <Phone size={20} className="mt-0.5 shrink-0 text-cyan-300" aria-hidden="true" />
                                <div>
                                    <h3 className="text-sm font-semibold">Mobile</h3>
                                    <a href="tel:+918971740472" className="mt-1 block text-sm text-slate-400 transition-colors duration-200 hover:text-white" data-testid="contact-phone-link">
                                        +91 89717 40472
                                    </a>
                                </div>
                            </div>
                            <div className="glass flex gap-4 rounded-2xl p-5" data-testid="contact-email-card">
                                <Mail size={20} className="mt-0.5 shrink-0 text-cyan-300" aria-hidden="true" />
                                <div>
                                    <h3 className="text-sm font-semibold">Email</h3>
                                    <p className="mt-1 text-sm text-slate-400">
                                        <span className="text-slate-500">General enquiries — </span>
                                        <a href="mailto:hello@innovatedglobal.in" className="transition-colors duration-200 hover:text-white" data-testid="contact-email-link">
                                            hello@innovatedglobal.in
                                        </a>
                                    </p>
                                    <p className="mt-1 text-sm text-slate-400">
                                        <span className="text-slate-500">Support — </span>
                                        <a href="mailto:support@innovatedglobal.in" className="transition-colors duration-200 hover:text-white" data-testid="contact-email-support-link">
                                            support@innovatedglobal.in
                                        </a>
                                    </p>
                                </div>
                            </div>
                            <div className="glass flex gap-4 rounded-2xl p-5" data-testid="contact-support-card">
                                <Clock size={20} className="mt-0.5 shrink-0 text-cyan-300" aria-hidden="true" />
                                <div>
                                    <h3 className="text-sm font-semibold">Support availability</h3>
                                    <p className="mt-1 text-sm leading-relaxed text-slate-400">
                                        24×7 support for active engagements, across India, the US, the Middle East,
                                        the UK, Europe and Australia.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-3">
                        <ContactForm key={validService} defaultService={validService} />
                    </Reveal>
                </div>
            </section>
        </>
    );
}
